import { ApolloError } from "@apollo/client";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import useApplyErrorToForm from "~/modules/app/hooks/useApplyErrorToForm";

import ObservationTypeFunctionTypeSelectElement from "../ObservationTypeFunctionTypeSelectElement";

type Props = {
    onSubmit: (formInput: ObservationTypeFunctionTypeFormInput) => void;
    isLoading?: boolean;
    researchTypeId: string;
    observationTypeId: string;
    error?: ApolloError;
};

export default function ObservationTypeFunctionTypeForm({
    onSubmit,
    isLoading,
    error,
    researchTypeId,
    observationTypeId,
}: Props) {
    const { t } = useTranslation("observationType");

    const methods = useForm<ObservationTypeFunctionTypeFormInput>({
        defaultValues: { ecologicalFunctionTypeId: "" },
    });

    const {
        handleSubmit,
        setError,
        formState: { errors, isDirty, isSubmitting, isValid },
    } = methods;

    useApplyErrorToForm(error, setError);

    return (
        <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Stack gap={2} sx={{ width: "400px" }}>
                    {errors.root && (
                        <Alert severity="error">
                            <Typography>{errors.root.message}</Typography>
                        </Alert>
                    )}

                    <ObservationTypeFunctionTypeSelectElement
                        researchTypeId={researchTypeId}
                        observationTypeId={observationTypeId}
                    />

                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={!isDirty || !isValid || isSubmitting || isLoading}
                        loading={isSubmitting || isLoading}
                    >
                        {t("observationTypeFunctionTypeForm.btnSubmit")}
                    </Button>
                </Stack>
            </form>
        </FormProvider>
    );
}

export type ObservationTypeFunctionTypeFormInput = {
    ecologicalFunctionTypeId: string;
};
